import React, { useState, SyntheticEvent } from 'react';
import { Row, Col, List, Avatar, Collapse, Typography, Tag, Button, Input, AutoComplete, Tooltip } from 'antd';
import useGlobal from '../../../store/Store';
import {
  getBackgroundColorForInboxResultField,
  getColorForInboxResultField,
  getIconForInboxResultField,
} from '../../../lib/util';
import { isReliableAndNotOnDocument } from '../../../lib/util/status';
import { updateInboxFileStatus } from '../../../lib/api/inbox-api';
import { InboxFile } from '../../../types';
import { Label } from '../../../types/label';
import { ResultField } from '../../../types/result';
import { SectionSubTitle, SectionTitle } from '../../../components/text/Titles';
import { Icon } from '@ant-design/compatible';
import { Section, SectionRow } from '../../../components/Section';

const { Text } = Typography;
const { Panel } = Collapse;

type Props = {
  inboxFile: InboxFile;
  inboxFileStatus: string;
  getStatusFromFile: any;
  onPropertiesChange?: (properties: { tag: string; annotationState: string }) => any;
};

const ResultSideBar = (props: Props) => {
  const { inboxFile, inboxFileStatus, getStatusFromFile, onPropertiesChange } = props;

  const [globalState, globalActions] = useGlobal();
  const [hoveredResultField, setHoveredResultField] = useState<null | ResultField>(null);
  let s = '';
  if (globalState.properties) s = globalState.properties.tag;
  const [tag, setTag] = useState<string>(s);

  const onLabelValueMouseEnter = (resultField: ResultField, label: Label) => {
    setHoveredResultField(resultField);
    globalActions.setLabelSelected(label);
  };

  const onLabelValueMouseLeave = () => {
    setHoveredResultField(null);
    globalActions.setLabelSelected(null);
  };

  const onPropertyChange = (properties: { tag: string; annotationState: string }) => {
    globalActions.setProperties(properties);
    if (onPropertiesChange) onPropertiesChange(properties);
  };

  const onTagChange = (value: SyntheticEvent<HTMLInputElement>) => {
    setTag(value.currentTarget.value);
  };

  const saveTag = (value: string) => {
    globalActions.setTagEdit(false);
    let props = globalState.properties;
    if (props && props.tag === value) return;
    if (props && onPropertyChange) {
      props.tag = value;
      props.annotationState = 'ONGOING';
      onPropertyChange(props);
      if (!globalState.tags.includes(value)) {
        let tags = globalState.tags;
        tags.push(value);
        globalActions.setTags(tags);
      }
    }
  };

  const handleKeyDown = (event: any) => {
    if (event.key === 'Enter') saveTag(tag);
  };

  const getPropertySettings = () => {
    const autocompleteoptions = {
      value: tag,
      dataSource: globalState.tags.filter(tag => typeof tag === 'string' && tag.length > 0),
      onChange: (value: any) => {
        setTag(value);
      },
      onSelect: (value: any) => {
        setTag(value);
        saveTag(value);
      },
      filterOption: (inputValue: any, option: any) => {
        return option.props.children.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1;
      },
    };

    if (globalState.properties)
      return (
        <>
          <SectionTitle>Properties</SectionTitle>
          <SectionRow className="items-center">
            <Col span={4}>
              <SectionSubTitle>Tag</SectionSubTitle>
            </Col>
            <Col span={18}>
              {globalState.tagEdit ? (
                <>
                  <AutoComplete {...autocompleteoptions}>
                    <Input style={{ width: '200px' }} onKeyDown={handleKeyDown} onChange={onTagChange} value={tag} />
                  </AutoComplete>
                  <Button
                    onClick={() => {
                      globalActions.setTagEdit(false);
                    }}>
                    <Icon type="close" />
                  </Button>
                </>
              ) : (
                <Row className="items-center">
                  <Tooltip title={globalState.properties.tag === '' ? 'Unlabeled' : globalState.properties.tag}>
                    <Tag style={{ width: '200px', overflowX: 'hidden', textOverflow: 'ellipsis', textAlign: 'center' }}>
                      {globalState.properties.tag === '' ? 'Unlabeled' : globalState.properties.tag}
                    </Tag>
                  </Tooltip>
                  <Button
                    onClick={() => {
                      if (globalState.properties) setTag(globalState.properties.tag);
                      globalActions.setTagEdit(true);
                    }}>
                    <Icon type="edit" />
                  </Button>
                </Row>
              )}
            </Col>
          </SectionRow>
          {inboxFile?.feedback?.properties?.length && (
            <>
              {inboxFile.feedback.properties.map(property => {
                return (
                  <SectionRow key={property.name} className="w-full items-center">
                    <Col span={6}>
                      <SectionSubTitle>{property.name}</SectionSubTitle>
                    </Col>
                    <Col span={18}>
                      <Tag color={property.correct ? 'green' : 'red'}>{property.correct ? 'correct' : 'incorrect'}</Tag>
                      {!property.correct && property.correction && (
                        <>
                          <Text className="ml-4 mr-2">Correction:</Text>
                          <Tag color="green">
                            {typeof property.correction === 'string' ? property.correction : property.correction.word}
                          </Tag>
                        </>
                      )}
                    </Col>
                  </SectionRow>
                );
              })}
            </>
          )}
        </>
      );
  };

  const renderResult = (resultField: ResultField) => {
    const resultLabel = globalState.labels.find(l => l.id === resultField.id)!;
    const correctionLabel = globalState.labels.find(l => l.id === resultField.id + '1');

    return (
      <List
        itemLayout="horizontal"
        dataSource={[resultField]}
        renderItem={resultField => (
          <List.Item
            onMouseEnter={() => onLabelValueMouseEnter(resultField, resultLabel)}
            onMouseLeave={() => onLabelValueMouseLeave()}
            onClick={() => {}}
            style={{
              cursor: 'pointer',
              padding: '4px 0',
            }}>
            <List.Item.Meta
              avatar={
                <Avatar
                  className={
                    (resultLabel || correctionLabel) && hoveredResultField && resultField.id === hoveredResultField.id
                      ? correctionLabel && !resultLabel.exists
                        ? 'connectLineD'
                        : 'connectLineB'
                      : ''
                  }
                  shape="square"
                  icon={
                    <Icon
                      type={getIconForInboxResultField(resultField)}
                      style={{
                        color: getColorForInboxResultField(resultField),
                        zIndex: 5000,
                      }}
                    />
                  }
                  style={{
                    backgroundColor: getBackgroundColorForInboxResultField(resultField),
                    cursor: 'pointer',
                  }}
                />
              }
              title={<span>{resultField.name}</span>}
              description={resultLabel && renderValue(resultLabel)}
            />
          </List.Item>
        )}
      />
    );
  };

  const renderValue = (label: Label) => {
    const notOnDocument = isReliableAndNotOnDocument(label);
    return (
      <Row>
        <Col span={13}>{!notOnDocument && <span>{label.sourceValue || label.word}</span>}</Col>
      </Row>
    );
  };

  const toggleFileStatus = async (file: InboxFile, inboxFileStatus: string) => {
    const newStatus = inboxFileStatus === 'NEW' ? 'STAGED' : 'NEW';
    await updateInboxFileStatus(file.id, newStatus);
    return getStatusFromFile(file.id);
  };

  return (
    <>
      <Section>{globalState.properties && getPropertySettings()}</Section>
      <Section>
        <SectionTitle>File status</SectionTitle>
        <SectionRow>
          <span>
            <Tag color={inboxFileStatus === 'NEW' ? 'red' : 'green'}>{inboxFileStatus}</Tag>
          </span>
          <Button type="link" size="small" onClick={() => toggleFileStatus(inboxFile, inboxFileStatus)}>
            Set to {inboxFileStatus === 'NEW' ? 'Staged' : 'New'}
          </Button>
        </SectionRow>
      </Section>
      <Row className="px-5">
        <SectionTitle>Results</SectionTitle>
      </Row>
      <Row>
        <Col span={24}>
          <Collapse style={{ backgroundColor: 'white' }} expandIconPosition={'right'} bordered={false}>
            {inboxFile.resultFields
              .sort((a, b) => (!!a.hasCorrection === !!b.hasCorrection ? 0 : !!a.hasCorrection ? -1 : 1))
              .map((it: ResultField) => {
                const data = [
                  !it.exists
                    ? {
                        id: it.id,
                        left: 'Test result',
                        valid: it.hasCorrection && it.hasCorrection.correct ? true : false,
                        right: `Not found`,
                        color: it.hasCorrection && it.hasCorrection.correct ? '#52c41a' : '#ccc',
                      }
                    : {
                        id: it.id,
                        left: 'Test result',
                        valid: it.hasCorrection ? it.hasCorrection.correct : it.reliable,
                        right: `"${it.positions[0].originalWord || it.positions[0].word}"`,
                        color: it.hasCorrection
                          ? it.hasCorrection.correct
                            ? '#52c41a'
                            : '#fa541c'
                          : it.reliable
                          ? '#52c41a'
                          : '#fa541c',
                      },
                  it.hasCorrection &&
                    !it.hasCorrection.correct && {
                      id: it.id + '1',
                      left: 'Correction',
                      valid: true,
                      right: `${
                        !it.hasCorrection.correction?.exists
                          ? 'not found'
                          : '"' + it.hasCorrection.correction?.word + '"'
                      }`,
                      color: '#52c41a',
                    },
                ];

                return (
                  <Panel
                    style={{
                      backgroundColor:
                        hoveredResultField && it.id === hoveredResultField.id ? '#e6f7ff' : 'transparent',
                    }}
                    header={renderResult(it)}
                    key={it.name}>
                    <div
                      style={{
                        padding: '0px 24px',
                        backgroundColor:
                          hoveredResultField && it.id === hoveredResultField.id ? '#e6f7ff' : 'transparent',
                      }}>
                      Process
                      <List
                        size={'small'}
                        bordered={false}
                        split={false}
                        itemLayout="horizontal"
                        dataSource={data.filter(Boolean)}
                        renderItem={dataRow =>
                          dataRow && (
                            <List.Item key={dataRow.id}>
                              <List.Item.Meta
                                title={dataRow.left}
                                avatar={
                                  <Avatar
                                    style={{
                                      backgroundColor: 'transparent',
                                    }}
                                    size={'small'}
                                    icon={
                                      <Icon
                                        type={dataRow.valid ? 'check' : 'exclamation-circle'}
                                        style={{
                                          color: dataRow.color,
                                        }}
                                      />
                                    }
                                  />
                                }
                                style={{ color: dataRow.color }}
                              />
                              <div style={{ color: dataRow.color }}>{dataRow.right}</div>
                            </List.Item>
                          )
                        }
                      />
                    </div>
                  </Panel>
                );
              })}
          </Collapse>
        </Col>
      </Row>
    </>
  );
};

export default ResultSideBar;
